import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Sparkles, Bot, Brain, MessageCircle, Lightbulb, TrendingUp } from 'lucide-react';

// Import AI components
import AIRuleBuilder from './ai/AIRuleBuilder';
import AIMappingSuggestions from './ai/AIMappingSuggestions';
import PatternLearningDashboard from './ai/PatternLearningDashboard';
import ConversationalAssistant from './ai/ConversationalAssistant';

const AIAssistant = ({ calculationId, accounts = [], onAccountUpdate }) => {
  const [activeTab, setActiveTab] = useState("chat");
  const [aiStats, setAiStats] = useState({
    rulesCreated: 0,
    patternsLearned: 0,
    automationRate: 0,
    accuracy: 0
  });

  const API = (process.env.REACT_APP_BACKEND_URL || "") || 'http://localhost:8001';

  useEffect(() => {
    if (calculationId) {
      loadAIStats();
    }
  }, [calculationId]);

  const loadAIStats = async () => {
    try {
      const response = await fetch(`${API}/api/client-patterns/analytics/current_client`);
      if (response.ok) {
        const data = await response.json();
        setAiStats({
          rulesCreated: data.stats?.rules_created || 0,
          patternsLearned: data.stats?.patterns_learned || 0,
          automationRate: data.stats?.automation_rate || 0,
          accuracy: data.stats?.accuracy || 0
        });
      }
    } catch (err) {
      console.error('Error loading AI stats:', err);
    }
  };

  const handleRuleCreated = (rule) => {
    // Refresh AI stats when a new rule is created
    loadAIStats();
  };

  const handleMappingUpdate = (update) => {
    // Propagate mapping updates to parent component
    if (onAccountUpdate) {
      onAccountUpdate(update);
    }
    // Refresh AI stats when mappings are updated
    loadAIStats();
  };

  return (
    <div className="space-y-6">
      {/* AI Assistant Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-500" />
            AI Assistant
          </h2>
          <p className="text-gray-600">
            Intelligent mapping assistance powered by GPT-4o and machine learning
          </p>
        </div>

        {/* AI Performance Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{aiStats.rulesCreated}</div>
            <div className="text-xs text-gray-600">Rules Created</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{aiStats.patternsLearned}</div>
            <div className="text-xs text-gray-600">Patterns Learned</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{aiStats.automationRate}%</div>
            <div className="text-xs text-gray-600">Automation Rate</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-indigo-600">{aiStats.accuracy}%</div>
            <div className="text-xs text-gray-600">AI Accuracy</div>
          </div>
        </div>
      </div>

      {/* AI Features Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="chat" className="flex items-center space-x-2">
            <MessageCircle className="h-4 w-4" />
            <span>AI Chat</span>
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center space-x-2">
            <Sparkles className="h-4 w-4" />
            <span>Rule Builder</span>
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="flex items-center space-x-2">
            <Lightbulb className="h-4 w-4" />
            <span>AI Suggestions</span>
          </TabsTrigger>
          <TabsTrigger value="learning" className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>Learning Progress</span>
          </TabsTrigger>
        </TabsList>

        {/* AI Chat Tab */}
        <TabsContent value="chat">
          <ConversationalAssistant calculationId={calculationId} />
        </TabsContent>

        {/* Rule Builder Tab */}
        <TabsContent value="rules">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold">Natural Language Rule Creation</h3>
                  <Badge variant="secondary">GPT-4o Powered</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Create sophisticated mapping rules by describing them in plain English
                </p>
              </CardHeader>
            </Card>
            
            <AIRuleBuilder onRuleCreated={handleRuleCreated} />
          </div>
        </TabsContent>

        {/* AI Suggestions Tab */}
        <TabsContent value="suggestions">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold">Intelligent Mapping Suggestions</h3>
                  <Badge variant="secondary">Pattern Learning</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Get AI-powered suggestions for unmapped accounts based on learned patterns
                </p>
              </CardHeader>
            </Card>
            
            <AIMappingSuggestions 
              calculationId={calculationId}
              accounts={accounts}
              onMappingUpdate={handleMappingUpdate}
            />
          </div>
        </TabsContent>

        {/* Learning Progress Tab */}
        <TabsContent value="learning">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-indigo-500" />
                  <h3 className="text-lg font-semibold">AI Learning Analytics</h3>
                  <Badge variant="secondary">Machine Learning</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Monitor how the AI learns from your decisions and improves over time
                </p>
              </CardHeader>
            </Card>
            
            <PatternLearningDashboard calculationId={calculationId} />
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Capabilities Overview */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-50">
        <CardContent className="p-6">
          <h4 className="font-semibold text-gray-900 mb-3">AI Capabilities</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-500" />
              <span>Natural Language Processing</span>
            </div>
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-blue-500" />
              <span>Pattern Recognition</span>
            </div>
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-green-500" />
              <span>Auto-mapping Suggestions</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <span>Continuous Learning</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started Tips */}
      {aiStats.patternsLearned === 0 && (
        <Alert className="border-blue-200 bg-blue-50">
          <Lightbulb className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Getting Started with AI:</strong> Start by mapping a few accounts manually or using the Rule Builder. 
            The AI will learn from your decisions and begin providing intelligent suggestions after analyzing your patterns.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AIAssistant;
